import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { Button, Input, Card, DateFormatSelector, LanguageSelector, LanguageSwitch } from '../shared';
import type { DateFormatPref } from '../../lib/date';
import { unitDefaultsForLocale } from '../lab-results/labUnitPref';
import {
  applyLanguage,
  loadOfferedLanguages,
  offeredLanguages,
  offeredLanguagesLoaded,
  offeredOrDefault,
} from '../../i18n';
import { storePreAccountLanguage, type Language } from '../../lib/language';

export const SignupPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // The field and the header switch are one choice: the language the page is in.
  const language = offeredOrDefault(i18n.language);
  const chooseLanguage = (lang: Language) => {
    storePreAccountLanguage(lang);
    applyLanguage(lang);
  };
  const { isAuthenticated, isLoading, error, signup, clearError } = useAuthStore();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateFormat, setDateFormat] = useState<DateFormatPref>('dd-mon-yyyy');
  const [validationErrors, setValidationErrors] = useState<{
    fullName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear store error when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  // The language field names itself in every offered language, so the page waits
  // until they are all loaded (i18n.ts loads only the one in use).
  const [languagesLoaded, setLanguagesLoaded] = useState(offeredLanguagesLoaded);
  useEffect(() => {
    if (languagesLoaded) return;
    let live = true;
    void loadOfferedLanguages().then(() => {
      if (live) setLanguagesLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [languagesLoaded]);

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!fullName.trim()) {
      errors.fullName = t('auth.signup.fullNameRequired');
    }

    if (!email.trim()) {
      errors.email = t('auth.signup.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('auth.signup.emailInvalid');
    }

    if (!password) {
      errors.password = t('auth.signup.passwordRequired');
    } else if (password.length < 8) {
      errors.password = t('auth.signup.passwordTooShort');
    }

    if (!confirmPassword) {
      errors.confirmPassword = t('auth.signup.confirmPasswordRequired');
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t('auth.signup.passwordsMismatch');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Lab and weight units start from the browser locale and the language chosen
      // (en-US → conventional and lb; Brazilian Portuguese → conventional and kg;
      // anywhere else → SI and kg); every entry form can change them.
      await signup(
        email, password, fullName, dateFormat, unitDefaultsForLocale(navigator.language, language), language,
      );
      navigate('/onboarding');
    } catch (error) {
      // Error is handled by the store and displayed via the error state
    }
  };

  if (!languagesLoaded) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-ivory flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <LanguageSwitch className="justify-end -mt-2 -mr-2 mb-2" />
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">🐾</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.brand.name')}</h1>
          <p className="text-sm text-gray-600 mt-2">{t('auth.brand.tagline')}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label={t('auth.signup.fullNameLabel')}
            name="fullName"
            type="text"
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setValidationErrors({ ...validationErrors, fullName: undefined });
            }}
            error={validationErrors.fullName}
            placeholder={t('auth.signup.fullNamePlaceholder')}
            required
            autoComplete="name"
          />

          <Input
            label={t('auth.signup.emailLabel')}
            name="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setValidationErrors({ ...validationErrors, email: undefined });
            }}
            error={validationErrors.email}
            placeholder={t('auth.signup.emailPlaceholder')}
            required
            autoComplete="email"
          />

          {/* "Language / Idioma": each offered language's own word, so the field
              reads to someone who has not yet found their language. A word two
              languages share is written once. */}
          <LanguageSelector
            value={language}
            onChange={chooseLanguage}
            label={[...new Set(offeredLanguages().map((lng) => t('auth.signup.languageLabel', { lng })))].join(' / ')}
          />

          <DateFormatSelector value={dateFormat} onChange={setDateFormat} />

          <Input
            label={t('auth.signup.passwordLabel')}
            name="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setValidationErrors({ ...validationErrors, password: undefined });
            }}
            error={validationErrors.password}
            placeholder={t('auth.signup.passwordPlaceholder')}
            required
            autoComplete="new-password"
            helperText={t('auth.signup.passwordHelper')}
          />

          <Input
            label={t('auth.signup.confirmPasswordLabel')}
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setValidationErrors({ ...validationErrors, confirmPassword: undefined });
            }}
            error={validationErrors.confirmPassword}
            placeholder={t('auth.signup.confirmPasswordPlaceholder')}
            required
            autoComplete="new-password"
          />

          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t('auth.signup.submit')}
          </Button>

          <p className="text-xs text-mid text-center">
            {t('auth.signup.nextStepNote')}
          </p>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            <Trans
              i18nKey="auth.signup.loginPrompt"
              components={{
                loginLink: (
                  <Link
                    to="/login"
                    className="text-sage-600 font-medium hover:text-sage-700 transition-colors"
                  />
                ),
              }}
            />
          </p>
        </div>
      </Card>
    </div>
  );
};
